import { inject, signal, Signal } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';

import {
  CreatePatientVisitInput,
  GqlService,
  UpdatePatientVisitInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { batchAPIRequest } from '@shared/utils/batch-promises';
import { gqlToPromise } from '@shared/utils/gql-to-promise';
import { Maybe } from '@shared/utils/utils';

const MAX_BATCH_SIZE = 50;

export const injectPatientVisits = ({
  enabled = signal(true),
  protocolVersionId,
}: {
  enabled?: Signal<boolean>;
  protocolVersionId: Signal<Maybe<string>>;
}) => {
  const mainQuery = inject(MainQuery);
  const gqlService = inject(GqlService);

  return injectQuery(() => ({
    enabled: enabled() && !!protocolVersionId(),
    queryFn: () => {
      return gqlToPromise(() =>
        gqlService.listPatientVisits$({
          patient_protocol_version_id: protocolVersionId(),
        })
      );
    },
    queryKey: ['trials', mainQuery.trialKey(), 'patient-visits', protocolVersionId()],
  }));
};

export const injectBatchCreatePatientVisits = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);

  return injectMutation(() => ({
    mutationFn: async (params: CreatePatientVisitInput[]) => {
      const { data, errors } = await batchAPIRequest(
        params,
        (input) => gqlService.batchCreatePatientVisits$(input),
        MAX_BATCH_SIZE
      );

      if (errors.length) {
        throw errors;
      }

      return data;
    },
    onError: (error: string[]) => {
      overlayService.error(error);
    },
  }));
};

export const injectBatchUpdatePatientVisits = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);

  return injectMutation(() => ({
    mutationFn: async (params: UpdatePatientVisitInput[]) => {
      const { data, errors } = await batchAPIRequest(
        params,
        (input) => gqlService.batchUpdatePatientVisits$(input),
        MAX_BATCH_SIZE
      );

      if (errors.length) {
        throw errors;
      }

      return data;
    },
    onError: (error: string[]) => {
      overlayService.error(error);
    },
  }));
};

export const refetchPatientVisits = () => {
  const mainQuery = inject(MainQuery);
  const queryClient = inject(QueryClient);

  return () => {
    void queryClient.invalidateQueries({
      queryKey: ['trials', mainQuery.trialKey(), 'patient-visits'],
    });
  };
};
